import { gql, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { OneDriveFspConfig } from '@work4all/models/lib/Classes/OneDriveFspConfig.entity';
import { WebDavFspConfig } from '@work4all/models/lib/Classes/WebDavFspConfig.entity';

export interface SharePointSite {
  id: string;
  name: string;
  url: string;
}

export type SharePointDrive = SharePointSite;

export interface FileServiceProviderConfigRootItem {
  itemId: string;
  itemType: string;
  key: string;
}

export const useFSPAccess = () => {
  const client = useApolloClient();

  const testOneDriveConfig = useCallback(
    async (
      sharePoint365Config: OneDriveFspConfig
    ): Promise<{ data: { testFileServiceProviderConfig: boolean } }> => {
      return await client.query({
        fetchPolicy: 'no-cache',
        query: TEST_ONE_DRIVE_PROVIDER_CONFIG,
        variables: {
          ...sharePoint365Config,
        },
      });
    },
    [client]
  );

  const listFileServiceProviderConfigRootItems = useCallback(
    async (
      sharePoint365Config: OneDriveFspConfig
    ): Promise<{
      data: {
        listFileServiceProviderConfigRootItems: FileServiceProviderConfigRootItem[];
      };
    }> => {
      return await client.query({
        fetchPolicy: 'no-cache',
        query: LIST_FILE_SERVICE_PROVIDER_CONFIG_ROOT_ITEMS,
        variables: {
          ...sharePoint365Config,
        },
      });
    },
    [client]
  );

  const listSharePointSites = useCallback(
    async (
      sharePoint365Config: Pick<
        OneDriveFspConfig,
        'clientId' | 'clientSecret' | 'tenantId'
      >
    ): Promise<{ data: { listSharePointSites: SharePointSite[] } }> => {
      return await client.query({
        fetchPolicy: 'no-cache',
        query: LIST_SHARE_POINT_SITES,
        variables: {
          ...sharePoint365Config,
        },
      });
    },
    [client]
  );

  const listSharePointDrives = useCallback(
    async (
      sharePoint365Config: Pick<
        OneDriveFspConfig,
        'clientId' | 'clientSecret' | 'tenantId' | 'siteId'
      >
    ): Promise<{ data: { listSharePointDrives: SharePointDrive[] } }> => {
      return await client.query({
        fetchPolicy: 'no-cache',
        query: LIST_SHARE_POINT_DRIVES,
        variables: {
          ...sharePoint365Config,
        },
      });
    },
    [client]
  );

  const testWebDavConfig = useCallback(
    async (
      webDavFspConfig: WebDavFspConfig
    ): Promise<{ data: { testFileServiceProviderConfig: boolean } }> => {
      return await client.query({
        fetchPolicy: 'no-cache',
        query: TEST_WEB_DAV_CONFIG,
        variables: {
          ...webDavFspConfig,
        },
      });
    },
    [client]
  );

  return {
    testOneDriveConfig,
    testWebDavConfig,
    listSharePointSites,
    listSharePointDrives,
    listFileServiceProviderConfigRootItems,
  };
};

const TEST_ONE_DRIVE_PROVIDER_CONFIG = gql`
  query testOneDriveConfig(
    $clientId: String
    $clientSecret: String
    $driveId: String
    $siteId: String
    $tenantId: String
  ) {
    testFileServiceProviderConfig(
      sharePoint365Config: {
        clientId: $clientId
        clientSecret: $clientSecret
        driveId: $driveId
        siteId: $siteId
        tenantId: $tenantId
      }
    )
  }
`;

const TEST_WEB_DAV_CONFIG = gql`
  query testWebDavConfig(
    $address: String!
    $username: String!
    $password: String!
  ) {
    testFileServiceProviderConfig(
      webDavConfig: {
        address: $address
        username: $username
        password: $password
      }
    )
  }
`;

const LIST_SHARE_POINT_SITES = gql`
  query listSharePointSites(
    $clientId: String!
    $clientSecret: String!
    $tenantId: String!
  ) {
    listSharePointSites(
      sharePoint365Config: {
        clientId: $clientId
        clientSecret: $clientSecret
        tenantId: $tenantId
      }
    ) {
      id
      name
      url
    }
  }
`;

const LIST_SHARE_POINT_DRIVES = gql`
  query listSharePointDrives(
    $clientId: String!
    $clientSecret: String!
    $tenantId: String!
    $siteId: String!
  ) {
    listSharePointDrives(
      sharePoint365Config: {
        clientId: $clientId
        clientSecret: $clientSecret
        tenantId: $tenantId
        siteId: $siteId
      }
    ) {
      id
      name
      url
    }
  }
`;

const LIST_FILE_SERVICE_PROVIDER_CONFIG_ROOT_ITEMS = gql`
  query listFileServiceProviderConfigRootItems(
    $clientId: String!
    $clientSecret: String!
    $driveId: String!
    $siteId: String!
    $tenantId: String!
  ) {
    listFileServiceProviderConfigRootItems(
      sharePoint365Config: {
        clientId: $clientId
        clientSecret: $clientSecret
        driveId: $driveId
        siteId: $siteId
        tenantId: $tenantId
      }
    ) {
      itemId
      itemType
      key
    }
  }
`;
